import { mockTenantConfigs } from './mock/tenants';
import {
  DEFAULT_TENANT_KEY,
  normalizeTenantKey,
  resolveTenantFromHostname,
} from './resolveTenantFromHostname';
import {
  ResolvedTenantConfig,
  ResolveTenantOptions,
  TenantConfig,
  TenantEnvironment,
  TenantEnvironmentUrls,
} from './types';

const loadMockTenantConfig = async (tenantKey: string) =>
  mockTenantConfigs[tenantKey] || mockTenantConfigs[DEFAULT_TENANT_KEY];

export const getFallbackTenantConfig = () =>
  mockTenantConfigs[DEFAULT_TENANT_KEY];

export const getTenantConfigByKey = (tenantKey: string): TenantConfig => {
  const normalizedKey = normalizeTenantKey(tenantKey);
  return mockTenantConfigs[normalizedKey] || getFallbackTenantConfig();
};

export const resolveActiveTenantKey = (options?: ResolveTenantOptions) =>
  resolveTenantFromHostname(options);

export const loadTenantConfig = async (tenantKey: string) =>
  loadMockTenantConfig(normalizeTenantKey(tenantKey));

export const resolveTenantEnvironment = (
  hostname = window.location.hostname
): TenantEnvironment => {
  const normalizedHostname = hostname.toLowerCase();

  if (
    normalizedHostname.includes('localhost') ||
    normalizedHostname.includes('127.0.0.1') ||
    normalizedHostname.includes('dev')
  ) {
    return 'development';
  }

  if (
    normalizedHostname.startsWith('stg.') ||
    normalizedHostname.includes('.stg.')
  ) {
    return 'staging';
  }

  return 'production';
};

export const getTenantEnvironmentUrl = (
  urls?: TenantEnvironmentUrls,
  hostname = window.location.hostname
) => {
  if (!urls) {
    return undefined;
  }

  const environment = resolveTenantEnvironment(hostname);

  return (
    urls[environment] || urls.production || urls.staging || urls.development
  );
};

export const getRuntimeTenantConfig = (options?: ResolveTenantOptions) => {
  const tenantKey = resolveActiveTenantKey(options);
  return getTenantConfigByKey(tenantKey);
};

export const getRuntimeTenantUrl = (
  urlKey: 'api' | 'observability' | 'security' | 'portal',
  hostname = window.location.hostname
) => {
  const tenantConfig = getRuntimeTenantConfig({ hostname });
  return getTenantEnvironmentUrl(tenantConfig.urls?.[urlKey], hostname);
};

export const getRuntimeTenantPortalUrl = (
  hostname = window.location.hostname
) => getRuntimeTenantUrl('portal', hostname);

const getConfiguredServiceOrigins = () => {
  const serviceUrls = [
    process.env.REACT_APP_USER_SERVICE,
    process.env.REACT_APP_BILLING_SERVICE,
    process.env.REACT_APP_REPORT_SERVICE,
    process.env.REACT_APP_NOTIFICATION_SERVICE,
  ].filter(Boolean) as string[];

  return serviceUrls
    .map((serviceUrl) => {
      try {
        return new URL(serviceUrl).origin;
      } catch (error) {
        return '';
      }
    })
    .filter(Boolean);
};

export const rewriteUrlForActiveTenantApi = (
  url?: string,
  hostname = window.location.hostname
) => {
  if (!url || !/^https?:\/\//i.test(url)) {
    return url;
  }

  const tenantApiUrl = getRuntimeTenantUrl('api', hostname);

  if (!tenantApiUrl) {
    return url;
  }

  const allowedOrigins = getConfiguredServiceOrigins();

  try {
    const parsedUrl = new URL(url);

    if (!allowedOrigins.includes(parsedUrl.origin)) {
      return url;
    }

    const tenantApiOrigin = new URL(tenantApiUrl);
    parsedUrl.protocol = tenantApiOrigin.protocol;
    parsedUrl.host = tenantApiOrigin.host;

    return parsedUrl.toString();
  } catch (error) {
    return url;
  }
};

export const loadActiveTenantConfig = async (
  options?: ResolveTenantOptions
): Promise<ResolvedTenantConfig> => {
  const requestedTenantKey = resolveActiveTenantKey(options);
  const tenantConfig = await loadTenantConfig(requestedTenantKey);

  return {
    requestedTenantKey,
    activeTenantKey: tenantConfig.key,
    tenantConfig,
  };
};
